import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { pluginAPI } from '../utils/api';
import { MAX_FILE_SIZE } from '../utils/formatFileSize';

const ACCEPTED_EXTENSIONS = '.mq5,.ex5,.zip,.rar';
const ACCEPTED_TYPES_RE = /\.(mq5|ex5|zip|rar)$/i;

const PluginUploadZone = ({ onUploadSuccess }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');

  const handleUpload = async (file) => {
    if (!file) return;

    if (!name.trim()) {
      const msg = t('plugins.error.nameRequired');
      setError(msg);
      toast.error(msg);
      return;
    }
    if (!description.trim()) {
      const msg = t('plugins.error.descriptionRequired');
      setError(msg);
      toast.error(msg);
      return;
    }

    if (!ACCEPTED_TYPES_RE.test(file.name)) {
      const msg = t('plugins.error.fileType');
      setError(msg);
      toast.error(msg);
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      const msg = t('plugins.error.fileSize');
      setError(msg);
      toast.error(msg);
      return;
    }

    setError('');
    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name.trim());
    formData.append('description', description.trim());

    try {
      const response = await pluginAPI.upload(formData, (p) => setProgress(p));
      toast.success(t('plugins.uploadSuccess'));
      onUploadSuccess?.(response.data.data);
      setName('');
      setDescription('');
      setProgress(0);
    } catch (err) {
      const msg = err.response?.data?.message || t('plugins.error.uploadFailed');
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onDragOver = (e) => {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length === 0) return;
    if (files.length > 1) {
      toast.warning(t('plugins.error.multipleFiles'));
    }
    handleUpload(files[0]);
  };
  const onFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) handleUpload(files[0]);
    e.target.value = '';
  };
  const onClick = () => {
    if (!uploading) fileInputRef.current?.click();
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
        <div>
          <label htmlFor="plugin-name" className="block text-sm font-medium text-gray-700 mb-1.5">
            {t('plugins.name')} <span className="text-red-500">*</span>
          </label>
          <input
            id="plugin-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            placeholder={t('plugins.name')}
            disabled={uploading}
            className="form-input w-full"
          />
        </div>
        <div>
          <label htmlFor="plugin-description" className="block text-sm font-medium text-gray-700 mb-1.5">
            {t('plugins.description')} <span className="text-red-500">*</span>
          </label>
          <textarea
            id="plugin-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1000}
            rows={3}
            placeholder={t('plugins.description')}
            disabled={uploading}
            className="form-input w-full resize-y"
          />
        </div>
      </div>
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
          isDragging
            ? 'border-primary-500 bg-primary-50 scale-[1.01]'
            : error
            ? 'border-red-400 bg-red-50'
            : 'border-gray-300 bg-gray-50 hover:border-primary-400 hover:bg-primary-50/30'
        } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
        data-testid="plugin-upload-zone"
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS}
          onChange={onFileInputChange}
          className="hidden"
        />
        {uploading ? (
          <div>
            <Loader2 className="w-12 h-12 mx-auto mb-3 text-primary-600 animate-spin" />
            <p className="font-semibold text-gray-900">{t('plugins.uploading')}</p>
            <div className="mt-3 max-w-xs mx-auto bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-primary-600 h-full transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-2">{t('plugins.uploadProgress', { percent: progress })}</p>
          </div>
        ) : (
          <div>
            <Upload
              className={`w-12 h-12 mx-auto mb-3 ${isDragging ? 'text-primary-600' : 'text-gray-400'}`}
            />
            <p className="font-semibold text-gray-900 mb-1">
              {isDragging ? t('plugins.uploadDragOver') : t('plugins.uploadTitle')}
            </p>
            <p className="text-sm text-gray-500">{t('plugins.uploadHint')}</p>
            <p className="text-xs text-gray-400 mt-3">{t('plugins.uploadAccepted')}</p>
            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default PluginUploadZone;